import { useCallback, useSyncExternalStore } from 'react';

/**
 * Subscribe to a CSS media query from JavaScript.
 *
 * Use this only when a breakpoint has to change *what renders*, not just how it
 * looks — styling belongs in a stylesheet. Reading through `useSyncExternalStore`
 * rather than `useState` + `useEffect` means the first render already has the
 * right answer, so there is no frame where the wrong branch is on screen.
 *
 * @param {string} query A media query string, e.g. `(min-width: 56rem)`.
 * @returns {boolean} Whether the query currently matches.
 */
export function useMediaQuery(query) {
  const subscribe = useCallback(
    (onChange) => {
      const list = window.matchMedia(query);
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    [query],
  );

  const getSnapshot = useCallback(() => window.matchMedia(query).matches, [query]);

  // Without a DOM there is no viewport to measure, so report "no match" rather
  // than throwing.
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

export default useMediaQuery;
